import { useState, useCallback, useEffect, useRef } from 'react';
import { Shield, Lock, AlertTriangle, CheckCircle, XCircle, Info, Calendar } from 'lucide-react';
import type { ToolComponentProps } from './index';

interface CertificateInfo {
  subject: string;
  issuer: string;
  validFrom: string;
  validTo: string;
  daysRemaining: number;
}

interface SSLResult {
  certificate: CertificateInfo | null;
  error: string | null;
}

interface TLSVersionResult {
  version: string;
  supported: boolean;
  cipher: string | null;
  error: string | null;
}

interface CipherResult {
  name: string;
  supported: boolean;
  version: string | null;
  strength: 'strong' | 'medium' | 'weak';
}

interface TLSResult {
  host: string;
  port: number;
  timestamp: string;
  grade: 'A+' | 'A' | 'B' | 'C' | 'D' | 'F';
  issues: string[];
  tlsVersions: TLSVersionResult[];
  ciphers: CipherResult[];
  ciphersByVersion: Record<string, CipherResult[]>;
  cipherStats: {
    total: number;
    strong: number;
    medium: number;
    weak: number;
  };
}

// In production, nginx serves both frontend and API from same origin
const API_BASE = '';

export function TLSScanner({ initialValue }: ToolComponentProps) {
  const [input, setInput] = useState(initialValue || '');
  const [result, setResult] = useState<TLSResult | null>(null);
  const [certInfo, setCertInfo] = useState<CertificateInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const hasAutoTriggered = useRef(false);

  const handleCheck = useCallback(async () => {
    let host = input.trim();
    if (!host) {
      setError('Please enter a hostname');
      return;
    }

    // Strip protocol if provided
    host = host.replace(/^https?:\/\//, '').split('/')[0].split(':')[0];

    setIsLoading(true);
    setError(null);
    setResult(null);
    setCertInfo(null);

    try {
      // Fetch TLS info and certificate info in parallel
      const [tlsResponse, sslResponse] = await Promise.all([
        fetch(`${API_BASE}/api/tls/${encodeURIComponent(host)}`),
        fetch(`${API_BASE}/api/ssl/${encodeURIComponent(host)}`)
      ]);
      
      const tlsData = await tlsResponse.json();
      const sslData: SSLResult = await sslResponse.json();

      if (!tlsResponse.ok) {
        setError(tlsData.error || 'TLS scan failed');
        return;
      }

      setResult(tlsData);
      
      // Set certificate info if available
      if (sslData.certificate) {
        setCertInfo(sslData.certificate);
      }

      // Update browser URL for sharing
      const params = new URLSearchParams();
      params.set('host', host);
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    } catch (err) {
      setError('Failed to connect to the server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [input]);

  // Auto-trigger check when initialValue is provided via URL
  useEffect(() => {
    if (initialValue && !hasAutoTriggered.current) {
      hasAutoTriggered.current = true;
      handleCheck();
    }
  }, [initialValue, handleCheck]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCheck();
    }
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A+':
      case 'A':
        return 'text-[var(--accent)] bg-[var(--accent-dim)]';
      case 'B':
        return 'text-blue-400 bg-blue-500/20';
      case 'C':
        return 'text-orange-500 dark:text-orange-400 bg-orange-500/20';
      case 'D':
        return 'text-orange-400 bg-orange-500/20';
      case 'F':
        return 'text-red-400 bg-red-500/20';
      default:
        return 'text-[var(--text-muted)] bg-[var(--bg-tertiary)]';
    }
  };

  const getVersionIcon = (version: string, supported: boolean) => {
    // TLS 1.0 and 1.1 are deprecated
    if (version === 'TLS 1.0' || version === 'TLS 1.1') {
      return supported ? (
        <AlertTriangle className="w-4 h-4 text-orange-500 dark:text-orange-400" />
      ) : (
        <CheckCircle className="w-4 h-4 text-[var(--accent)]" />
      );
    }
    // TLS 1.2 and 1.3 are good
    return supported ? (
      <CheckCircle className="w-4 h-4 text-[var(--accent)]" />
    ) : (
      <XCircle className="w-4 h-4 text-red-400" />
    );
  };

  const getVersionStatus = (version: string, supported: boolean) => {
    if (version === 'TLS 1.0' || version === 'TLS 1.1') {
      return supported ? 'Supported (Deprecated!)' : 'Not Supported (Good)';
    }
    return supported ? 'Supported' : 'Not Supported';
  };

  const getStrengthColor = (strength: string) => {
    switch (strength) {
      case 'strong':
        return 'text-[var(--accent)] bg-[var(--accent-dim)]';
      case 'medium':
        return 'text-orange-500 dark:text-orange-400 bg-orange-500/20';
      case 'weak':
        return 'text-red-400 bg-red-500/20';
      default:
        return 'text-[var(--text-muted)] bg-[var(--bg-tertiary)]';
    }
  };

  const renderOutput = () => {
    if (error) {
      return (
        <div className="text-red-400 p-4 bg-red-500/10 rounded-lg">
          <span className="font-semibold">Error:</span> {error}
        </div>
      );
    }

    if (!result) {
      return (
        <div className="text-[var(--text-muted)] text-center py-8">
          Enter a hostname above to scan its TLS configuration, supported versions, and cipher suites.
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Certificate Status Banner */}
        {certInfo && (
          <div className={`flex items-center gap-4 p-4 rounded-lg ${
            certInfo.daysRemaining <= 0
              ? 'bg-red-500/20 border border-red-500/30'
              : certInfo.daysRemaining <= 30
                ? 'bg-orange-500/20 border border-orange-500/30'
                : 'bg-[var(--accent-dim)] border border-[var(--accent)]/30'
          }`}>
            <Calendar className={`w-8 h-8 ${
              certInfo.daysRemaining <= 0
                ? 'text-red-500'
                : certInfo.daysRemaining <= 30
                  ? 'text-orange-500 dark:text-orange-400'
                  : 'text-[var(--accent)]'
            }`} />
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`font-semibold ${
                  certInfo.daysRemaining <= 0
                    ? 'text-red-500'
                    : certInfo.daysRemaining <= 30
                      ? 'text-orange-500 dark:text-orange-400'
                      : 'text-[var(--accent)]'
                }`}>
                  Server Certificate
                </span>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                  certInfo.daysRemaining <= 0
                    ? 'bg-red-500/30 text-red-500 dark:text-red-400'
                    : certInfo.daysRemaining <= 30
                      ? 'bg-orange-500/30 text-orange-600 dark:text-orange-400'
                      : 'bg-green-500/20 text-green-600 dark:text-green-400'
                }`}>
                  {certInfo.daysRemaining <= 0 
                    ? 'Expired' 
                    : certInfo.daysRemaining === 1 
                      ? '1 day left' 
                      : `${certInfo.daysRemaining} days left`}
                </span>
              </div>
              <div className="text-sm text-[var(--text-secondary)] mt-1">
                {certInfo.subject} • Valid until {new Date(certInfo.validTo).toLocaleDateString()}
              </div>
            </div>
          </div>
        )}

        {/* Grade and Summary */}
        <div className="flex items-start gap-6 p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
          <div className={`text-5xl font-bold px-4 py-2 rounded-lg ${getGradeColor(result.grade)}`}>
            {result.grade}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-lg text-[var(--text-primary)]">
              {result.host}:{result.port}
            </div>
            <div className="text-sm text-[var(--text-muted)] mt-1">
              Scanned at {new Date(result.timestamp).toLocaleString()}
            </div>
            {result.issues.length > 0 && (
              <div className="mt-3 space-y-1">
                {result.issues.map((issue, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-orange-500 dark:text-orange-400">
                    <AlertTriangle className="w-3 h-3 shrink-0" />
                    {issue}
                  </div>
                ))}
              </div>
            )}
            {result.issues.length === 0 && (
              <div className="flex items-center gap-2 text-sm text-[var(--accent)] mt-3">
                <CheckCircle className="w-4 h-4" />
                Excellent TLS configuration!
              </div>
            )}
          </div>
        </div>

        {/* TLS Versions */}
        <div>
          <h3 className="text-[var(--accent)] font-semibold mb-3 flex items-center gap-2">
            <Lock className="w-4 h-4" />
            TLS Protocol Versions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {result.tlsVersions.map((v) => (
              <div
                key={v.version}
                className={`p-3 rounded-lg border ${
                  v.supported
                    ? v.version === 'TLS 1.0' || v.version === 'TLS 1.1'
                      ? 'bg-orange-500/10 border-orange-500/30'
                      : 'bg-[var(--accent-dim)] border-[var(--accent)]/30'
                    : 'bg-[var(--bg-secondary)] border-[var(--border-color)]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{v.version}</span>
                  {getVersionIcon(v.version, v.supported)}
                </div>
                <div className="text-xs text-[var(--text-muted)] mt-1">
                  {getVersionStatus(v.version, v.supported)}
                </div>
                {v.supported && v.cipher && (
                  <div className="text-xs text-[var(--text-muted)] mt-1 font-mono truncate" title={v.cipher}>
                    {v.cipher}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Cipher Stats */}
        <div>
          <h3 className="text-[var(--accent)] font-semibold mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Cipher Suites Summary
          </h3>
          <div className="flex gap-4 p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
            <div className="text-center">
              <div className="text-2xl font-bold text-[var(--text-primary)]">{result.cipherStats.total}</div>
              <div className="text-xs text-[var(--text-muted)]">Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-[var(--accent)]">{result.cipherStats.strong}</div>
              <div className="text-xs text-[var(--text-muted)]">Strong</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-500 dark:text-orange-400">{result.cipherStats.medium}</div>
              <div className="text-xs text-[var(--text-muted)]">Medium</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-400">{result.cipherStats.weak}</div>
              <div className="text-xs text-[var(--text-muted)]">Weak</div>
            </div>
          </div>
        </div>

        {/* Cipher List Grouped by TLS Version */}
        {result.ciphersByVersion && Object.keys(result.ciphersByVersion).length > 0 && (
          <div>
            <h3 className="text-[var(--accent)] font-semibold mb-3 flex items-center gap-2">
              <Info className="w-4 h-4" />
              Supported Cipher Suites by TLS Version ({result.ciphers.length} total)
            </h3>
            <div className="space-y-4">
              {['TLSv1.3', 'TLSv1.2', 'TLSv1.1', 'TLSv1', 'Unknown'].map((version) => {
                const ciphers = result.ciphersByVersion[version];
                if (!ciphers || ciphers.length === 0) return null;
                
                const versionLabel = version === 'TLSv1.3' ? 'TLS 1.3' 
                  : version === 'TLSv1.2' ? 'TLS 1.2'
                  : version === 'TLSv1.1' ? 'TLS 1.1'
                  : version === 'TLSv1' ? 'TLS 1.0'
                  : version;
                
                const isDeprecated = version === 'TLSv1' || version === 'TLSv1.1';
                
                return (
                  <div key={version} className="rounded-lg border border-[var(--border-color)] overflow-hidden">
                    <div className={`px-4 py-2 font-semibold flex items-center justify-between ${
                      isDeprecated 
                        ? 'bg-orange-500/20 text-orange-500 dark:text-orange-400' 
                        : 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]'
                    }`}>
                      <span className="flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        {versionLabel}
                        {isDeprecated && <span className="text-xs">(Deprecated)</span>}
                      </span>
                      <span className="text-sm text-[var(--text-muted)]">{ciphers.length} cipher{ciphers.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="divide-y divide-[var(--border-color)]">
                      {ciphers.map((cipher, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between px-4 py-2 bg-[var(--bg-secondary)]"
                        >
                          <span className="font-mono text-sm">{cipher.name}</span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStrengthColor(cipher.strength)}`}>
                            {cipher.strength.toUpperCase()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" onKeyDown={handleKeyDown}>
      {/* Input Section */}
      <div className="space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-[var(--text-primary)]">
          Hostname
        </label>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <input
            type="text"
            className="flex-1 min-w-0 px-3 sm:px-4 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-sm"
            placeholder="example.com"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            spellCheck={false}
          />
          <button
            type="button"
            onClick={handleCheck}
            disabled={isLoading || !input.trim()}
            className="btn-primary flex items-center justify-center gap-2 px-4 sm:px-6 whitespace-nowrap"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Scanning...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4" />
                Scan TLS
              </>
            )}
          </button>
        </div>
      </div>

      {/* Output Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[var(--text-primary)]">
          TLS Analysis
        </label>
        <div className="output-panel">
          {renderOutput()}
        </div>
      </div>

      <div className="text-xs text-[var(--text-muted)]">
        <span className="text-[var(--accent)]">Note:</span> This tool tests TLS 1.0, 1.1, 1.2, and 1.3 support, and enumerates supported cipher suites. 
        TLS 1.0 and 1.1 are deprecated and should be disabled. The scan may take 15-30 seconds.
      </div>
    </div>
  );
}
